const FIRST_NAMES = [
    "Aiden", "Blake", "Carter", "Dylan", "Ethan", "Finn", "Grant", "Hayden",
    "Ivan", "Jace", "Kai", "Logan", "Mason", "Nolan", "Owen", "Parker",
    "Quinn", "Reid", "Sawyer", "Tristan", "Vance", "Wesley", "Xander", "Zane",
    "Brody", "Cole", "Dean", "Elias", "Felix", "Gavin", "Hunter", "Isaac",
    "Jasper", "Knox", "Levi", "Miles", "Nate", "Oscar", "Preston", "Ryder",
];

const LAST_NAMES = [
    "Anderson", "Bennett", "Campbell", "Donovan", "Ellis", "Foster", "Grayson",
    "Hayes", "Ingram", "Jensen", "Keller", "Lawson", "Mitchell", "Nash", "Olsen",
    "Porter", "Reeves", "Sullivan", "Thompson", "Vaughn", "Walker", "Young",
    "Barrett", "Crawford", "Dalton", "Everett", "Fleming", "Garrett", "Hartman",
    "Irving", "Jordan", "Kennedy", "Larson", "Murphy", "Norris", "Palmer",
    "Quincy", "Ramsey", "Sterling", "Turner", "Warren", "Whitman",
];

const CPU_TEAM_NAMES = [
    "North Bay Wolves",
    "Cedar Rapids Storm",
    "Lake Erie Thunder",
    "Prairie City Blades",
    "Summit Ridge Bears",
    "Harbor Point Otters",
    "Red River Hawks",
];

export const USER_TEAM_INDEX = 0;
export const DRAFT_ROUNDS = 4;
export const TEAM_COUNT = 8;
export const PROSPECT_POOL_SIZE = 100;

function shuffle(array) {
    const copy = [...array];
    for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

function buildFakeNamePool(size) {
    const combos = [];
    for (const first of FIRST_NAMES) {
        for (const last of LAST_NAMES) {
            combos.push(`${first} ${last}`);
        }
    }
    return shuffle(combos).slice(0, size);
}

export function anonymizeProspects(rawPlayers) {
    const fakeNames = buildFakeNamePool(rawPlayers.length);
    const identityMap = new Map();

    const prospects = rawPlayers.map((player, index) => {
        const anonId = `prospect-${index + 1}`;
        const fakeName = fakeNames[index] || `Prospect ${index + 1}`;
        const [fakeFirst, ...rest] = fakeName.split(" ");
        const fakeLast = rest.join(" ") || "Player";

        identityMap.set(anonId, {
            playerId: player.player_id,
            playerImageUrl: player.player_image_url,
            realFirstName: player.first_name,
            realLastName: player.last_name,
            realName: `${player.first_name} ${player.last_name}`,
            draftResult: player.draft_result,
            position: player.position,
        });

        const stats = (player.stats || []).map((row) => ({
            year: row.year,
            isPlayoff: row.is_playoff,
            leagueName: row.league_name,
            gamesPlayed: row.games_played,
            penaltyMinutes: row.penalty_minutes,
            goals: row.goals,
            assists: row.assists,
            goalsAgainstAverage: row.goals_against_average,
            savePercentage: row.save_percentage,
            shutouts: row.shutouts,
            record: row.record,
        }));

        const draftYear = player.draft_year;
        const isGoalie = isGoalieProspect(stats);
        const draftTotals = buildDraftTotals(stats, draftYear, isGoalie);

        return {
            anonId,
            fakeFirstName: fakeFirst,
            fakeLastName: fakeLast,
            fakeName,
            position: player.position || "—",
            height: player.height || "—",
            weight: player.weight || "—",
            handedness: player.handedness || "—",
            country: player.country?.country_name || "—",
            draftYear,
            stats,
            isGoalie,
            draftTotals,
            score: prospectScore(stats),
        };
    });

    return { prospects, identityMap };
}

export function draftSeasonForYear(draftYear) {
    const year = parseInt(draftYear, 10);
    if (!Number.isFinite(year)) return null;
    return `${year - 1}-${String(year).slice(-2)}`;
}

export function filterDraftSeasonStats(stats, draftYear, { includePlayoffs = false } = {}) {
    const season = draftSeasonForYear(draftYear);
    const seasonRows = stats.filter(
        (row) => row.year === season && (includePlayoffs || !row.isPlayoff)
    );
    if (seasonRows.length) return seasonRows;
    return stats.filter((row) => includePlayoffs || !row.isPlayoff);
}

function buildLeagueBreakdown(rows) {
    return rows.map((row) => ({
        leagueName: row.leagueName,
        gamesPlayed: row.gamesPlayed || 0,
        goals: row.goals || 0,
        assists: row.assists || 0,
        points: (row.goals || 0) + (row.assists || 0),
        goalsAgainstAverage: row.goalsAgainstAverage,
        savePercentage: row.savePercentage,
        shutouts: row.shutouts,
        record: row.record,
        penaltyMinutes: row.penaltyMinutes,
        isPlayoff: row.isPlayoff,
    }));
}

export function aggregateDraftSeasonSkater(stats, draftYear) {
    const rows = filterDraftSeasonStats(stats, draftYear);
    const gamesPlayed = rows.reduce((sum, row) => sum + (row.gamesPlayed || 0), 0);
    const goals = rows.reduce((sum, row) => sum + (row.goals || 0), 0);
    const assists = rows.reduce((sum, row) => sum + (row.assists || 0), 0);
    const points = goals + assists;
    return {
        gamesPlayed,
        goals,
        assists,
        points,
        pointsPerGame: gamesPlayed ? points / gamesPlayed : 0,
        leagueBreakdown: buildLeagueBreakdown(rows),
    };
}

export function aggregateDraftSeasonGoalie(stats, draftYear) {
    const rows = filterDraftSeasonStats(stats, draftYear);
    const gamesPlayed = rows.reduce((sum, row) => sum + (row.gamesPlayed || 0), 0);
    const shutouts = rows.reduce((sum, row) => sum + (row.shutouts || 0), 0);

    let weightedSv = 0;
    let weightedGaa = 0;
    let svWeight = 0;
    let gaaWeight = 0;
    rows.forEach((row) => {
        const gp = row.gamesPlayed || 0;
        const sv = parseFloat(row.savePercentage);
        const gaa = parseFloat(row.goalsAgainstAverage);
        if (Number.isFinite(sv) && sv > 0) {
            weightedSv += sv * gp;
            svWeight += gp;
        }
        if (Number.isFinite(gaa) && gaa > 0) {
            weightedGaa += gaa * gp;
            gaaWeight += gp;
        }
    });

    return {
        gamesPlayed,
        shutouts,
        savePercentage: svWeight ? weightedSv / svWeight : 0,
        goalsAgainstAverage: gaaWeight ? weightedGaa / gaaWeight : 0,
        leagueBreakdown: buildLeagueBreakdown(rows),
    };
}

export function buildDraftTotals(stats, draftYear, isGoalie) {
    if (isGoalie) {
        return { isGoalie: true, ...aggregateDraftSeasonGoalie(stats, draftYear) };
    }
    return { isGoalie: false, ...aggregateDraftSeasonSkater(stats, draftYear) };
}

export function formatPointsPerGame(value) {
    if (!Number.isFinite(value)) return "—";
    return value.toFixed(2);
}

export const DEFAULT_PROSPECT_SORT = { key: "fakeName", direction: "asc" };

const SORT_DEFAULT_DIRECTION = {
    starred: "desc",
    fakeName: "asc",
    position: "asc",
    country: "asc",
    gamesPlayed: "desc",
    points: "desc",
    pointsPerGame: "desc",
    savePercentage: "desc",
    goalsAgainstAverage: "asc",
};

export const DEFAULT_LIST_FILTERS = {
    country: "ALL",
    position: "ALL",
    type: "ALL",
    starred: "ALL",
    league: "ALL",
    leaguePlayed: "YES",
    minGp: "",
    minPts: "",
    minPpg: "",
    minSv: "",
    maxGaa: "",
};

export function getProspectLeagues(prospect) {
    const breakdown = prospect.draftTotals?.leagueBreakdown || [];
    return [...new Set(breakdown.map((row) => row.leagueName).filter(Boolean))];
}

export function prospectPlayedInLeague(prospect, leagueName) {
    if (!leagueName || leagueName === "ALL") return true;
    return getProspectLeagues(prospect).includes(leagueName);
}

export function collectProspectLeagues(prospects) {
    const set = new Set();
    prospects.forEach((prospect) => {
        getProspectLeagues(prospect).forEach((league) => set.add(league));
    });
    return Array.from(set).sort((left, right) => left.localeCompare(right));
}

export function getNextSortDirection(sortKey, currentKey, currentDirection) {
    if (currentKey === sortKey) {
        return currentDirection === "asc" ? "desc" : "asc";
    }
    return SORT_DEFAULT_DIRECTION[sortKey] || "asc";
}

export function getProspectSortValue(prospect, sortKey, starredIds) {
    const totals = prospect.draftTotals || {};
    switch (sortKey) {
    case "starred":
        return starredIds.has(prospect.anonId) ? 1 : 0;
    case "fakeName":
        return prospect.fakeName?.toLowerCase() || "";
    case "position":
        return prospect.position || "";
    case "country":
        return prospect.country || "";
    case "gamesPlayed":
        return totals.gamesPlayed || 0;
    case "points":
        return prospect.isGoalie ? -1 : (totals.points || 0);
    case "pointsPerGame":
        return prospect.isGoalie ? -1 : (totals.pointsPerGame || 0);
    case "savePercentage":
        return prospect.isGoalie ? (parseFloat(totals.savePercentage) || 0) : -1;
    case "goalsAgainstAverage":
        return prospect.isGoalie ? (parseFloat(totals.goalsAgainstAverage) || 999) : 999;
    default:
        return "";
    }
}

export function sortProspects(prospects, sortKey, sortDirection, starredIds) {
    const direction = sortDirection === "asc" ? 1 : -1;
    return [...prospects].sort((left, right) => {
        const leftValue = getProspectSortValue(left, sortKey, starredIds);
        const rightValue = getProspectSortValue(right, sortKey, starredIds);
        if (typeof leftValue === "number" && typeof rightValue === "number") {
            return (leftValue - rightValue) * direction;
        }
        return String(leftValue).localeCompare(String(rightValue)) * direction;
    });
}

export function applyProspectFilters(
    prospects,
    { search, ...filters },
    starredIds
) {
    const query = search?.trim().toLowerCase() || "";

    return prospects.filter((prospect) => {
        if (filters.position !== "ALL" && prospect.position !== filters.position) return false;
        if (filters.country !== "ALL" && prospect.country !== filters.country) return false;
        if (filters.type === "SKATER" && prospect.isGoalie) return false;
        if (filters.type === "GOALIE" && !prospect.isGoalie) return false;
        if (filters.starred === "STARRED" && !starredIds.has(prospect.anonId)) return false;

        if (filters.league && filters.league !== "ALL") {
            const played = prospectPlayedInLeague(prospect, filters.league);
            if (filters.leaguePlayed === "NO" ? played : !played) return false;
        }

        const totals = prospect.draftTotals || {};
        const gamesPlayed = totals.gamesPlayed || 0;
        const points = totals.points || 0;
        const pointsPerGame = totals.pointsPerGame || 0;
        const savePercentage = parseFloat(totals.savePercentage) || 0;
        const goalsAgainstAverage = parseFloat(totals.goalsAgainstAverage) || 0;

        if (filters.minGp !== "" && gamesPlayed < Number(filters.minGp)) return false;
        if (filters.minPts !== "" && (prospect.isGoalie || points < Number(filters.minPts))) return false;
        if (filters.minPpg !== "" && (prospect.isGoalie || pointsPerGame < Number(filters.minPpg))) return false;
        if (filters.minSv !== "" && (!prospect.isGoalie || savePercentage < Number(filters.minSv))) return false;
        if (filters.maxGaa !== "" && (!prospect.isGoalie || goalsAgainstAverage > Number(filters.maxGaa))) return false;

        if (!query) return true;
        return (
            prospect.fakeName.toLowerCase().includes(query)
            || prospect.position.toLowerCase().includes(query)
            || prospect.country.toLowerCase().includes(query)
        );
    });
}

export function isGoalieProspect(stats) {
    return stats.some((row) => {
        const sv = parseFloat(row.savePercentage);
        return Number.isFinite(sv) && sv > 0;
    });
}

export function prospectScore(stats) {
    if (!stats.length) return 0;
    if (isGoalieProspect(stats)) {
        const gp = stats.reduce((sum, row) => sum + (row.gamesPlayed || 0), 0);
        const svValues = stats
            .map((row) => parseFloat(row.savePercentage))
            .filter((v) => Number.isFinite(v) && v > 0);
        const gaaValues = stats
            .map((row) => parseFloat(row.goalsAgainstAverage))
            .filter((v) => Number.isFinite(v) && v > 0);
        const avgSv = svValues.length
            ? svValues.reduce((a, b) => a + b, 0) / svValues.length
            : 0;
        const avgGaa = gaaValues.length
            ? gaaValues.reduce((a, b) => a + b, 0) / gaaValues.length
            : 5;
        return gp * 0.4 + avgSv * 40 + Math.max(0, 3 - avgGaa) * 8;
    }

    let goals = 0;
    let assists = 0;
    let gp = 0;
    stats.forEach((row) => {
        goals += row.goals || 0;
        assists += row.assists || 0;
        gp += row.gamesPlayed || 0;
    });
    const points = goals + assists;
    const ppg = gp ? points / gp : 0;
    return points + ppg * 20 + gp * 0.05;
}

export function getTeamNames() {
    return ["Your Team", ...CPU_TEAM_NAMES.slice(0, TEAM_COUNT - 1)];
}

export function createRandomDraftOrder() {
    const order = Array.from({ length: TEAM_COUNT }, (_, index) => index);
    for (let i = order.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [order[i], order[j]] = [order[j], order[i]];
    }
    return order;
}

export function pickNumberToSlot(pickNumber, draftOrder) {
    const round = Math.ceil(pickNumber / TEAM_COUNT);
    const posInRound = (pickNumber - 1) % TEAM_COUNT;
    const teamIndex = draftOrder[posInRound];
    return { round, teamIndex, pickInRound: posInRound + 1 };
}

export function totalPicks() {
    return TEAM_COUNT * DRAFT_ROUNDS;
}

export function pickCpuProspect(availableProspects) {
    if (!availableProspects.length) return null;
    const ranked = [...availableProspects].sort((a, b) => b.score - a.score);
    const poolSize = Math.min(6, ranked.length);
    const pool = ranked.slice(0, poolSize);
    return pool[Math.floor(Math.random() * pool.length)];
}

export function formatStatValue(value) {
    if (value == null || value === "") return "—";
    return value;
}

export function formatSavePct(value) {
    const num = parseFloat(value);
    if (!Number.isFinite(num) || num <= 0) return "—";
    if (num >= 1) return num.toFixed(3);
    return `.${String(Math.round(num * 1000)).padStart(3, "0")}`;
}

export const NHL_LEAGUE_ID = "nhl";
export const RECENT_DRAFT_PROSPECT_YEARS = 5;
export const PROSPECT_LEAGUE_SCORE_MULTIPLIER = 0.35;

export function parseNhlStatsResponse(data) {
    if (typeof data === "string") {
        try {
            return JSON.parse(data);
        } catch {
            return [];
        }
    }
    return Array.isArray(data) ? data : [];
}

export function isNhlStatsRow(row) {
    if (row?.is_nhl === true) return true;

    const leagueId = String(row?.league_id || "").trim().toLowerCase();
    if (leagueId === NHL_LEAGUE_ID) return true;

    const abbrev = String(row?.nhl_api_league_abbrev || "").trim().toUpperCase();
    if (abbrev === "NHL") return true;

    const leagueName = String(row?.league_name || "").trim().toUpperCase();
    return leagueName === "NHL" || leagueName === "NATIONAL HOCKEY LEAGUE";
}

export function parseNhlCareerResponse(data) {
    let payload = data;
    if (typeof payload === "string") {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }
    if (!payload || !payload.games_played) return null;

    if (payload.is_goalie) {
        return {
            gamesPlayed: payload.games_played,
            savePercentage: payload.save_percentage,
            goalsAgainstAverage: payload.goals_against_average,
        };
    }

    return {
        gamesPlayed: payload.games_played,
        goals: payload.goals || 0,
        assists: payload.assists || 0,
        points: payload.points || 0,
        pointsPerGame: payload.points_per_game || 0,
    };
}

export function filterNhlStatsRows(stats) {
    return parseNhlStatsResponse(stats).filter(isNhlStatsRow);
}

export function postDraftSeasonStartYear(nhlSeason) {
    const season = parseInt(nhlSeason, 10);
    if (!Number.isFinite(season)) return null;
    return Math.floor(season / 10000);
}

export function isPostDraftStatsRow(row, draftYear) {
    const year = parseInt(draftYear, 10);
    const startYear = postDraftSeasonStartYear(row?.nhl_season);
    if (!Number.isFinite(year) || startYear == null) return false;
    return startYear >= year;
}

export function filterPostDraftNonNhlRows(stats, draftYear) {
    return parseNhlStatsResponse(stats).filter(
        (row) => !isNhlStatsRow(row) && isPostDraftStatsRow(row, draftYear)
    );
}

function summarizeCareerFromRows(source, isGoalie) {
    if (!source.length) return null;

    const gamesPlayed = source.reduce((sum, row) => sum + (row.games_played || 0), 0);

    if (isGoalie) {
        let weightedSv = 0;
        let weightedGaa = 0;
        let svWeight = 0;
        let gaaWeight = 0;
        source.forEach((row) => {
            const gp = row.games_played || 0;
            const sv = parseFloat(row.save_percentage);
            const gaa = parseFloat(row.goals_against_average);
            if (Number.isFinite(sv) && sv > 0) {
                weightedSv += sv * gp;
                svWeight += gp;
            }
            if (Number.isFinite(gaa) && gaa > 0) {
                weightedGaa += gaa * gp;
                gaaWeight += gp;
            }
        });
        return {
            gamesPlayed,
            savePercentage: svWeight ? weightedSv / svWeight : null,
            goalsAgainstAverage: gaaWeight ? weightedGaa / gaaWeight : null,
        };
    }

    const goals = source.reduce((sum, row) => sum + (row.goals || 0), 0);
    const assists = source.reduce((sum, row) => sum + (row.assists || 0), 0);
    const points = source.reduce((sum, row) => sum + (row.points ?? (row.goals || 0) + (row.assists || 0)), 0);
    return {
        gamesPlayed,
        goals,
        assists,
        points,
        pointsPerGame: gamesPlayed ? points / gamesPlayed : 0,
    };
}

export function summarizeNhlCareer(stats, isGoalie) {
    return summarizeCareerFromRows(filterNhlStatsRows(stats), isGoalie);
}

export function summarizePostDraftNonNhlCareer(stats, draftYear, isGoalie) {
    return summarizeCareerFromRows(filterPostDraftNonNhlRows(stats, draftYear), isGoalie);
}

export function groupPicksByTeam(picks, teamNames) {
    return teamNames.map((teamName, teamIndex) => ({
        teamIndex,
        teamName,
        picks: picks
            .filter((pick) => pick.teamIndex === teamIndex)
            .sort((left, right) => left.pickNumber - right.pickNumber),
    }));
}

export function actualOverallPick(draftResult) {
    if (!draftResult) return null;
    const round = parseInt(draftResult.round, 10);
    const pick = parseInt(draftResult.pick, 10);
    const draftYear = parseInt(draftResult.draft_year, 10);
    if (!Number.isFinite(round) || !Number.isFinite(pick) || !Number.isFinite(draftYear)) {
        return null;
    }
    let teamCount = 30;
    if (draftYear >= 2021) teamCount = 32;
    else if (draftYear >= 2017) teamCount = 31;
    return pick + (round - 1) * teamCount;
}

export function careerMaturityFactor(draftYear, currentYear = new Date().getFullYear()) {
    const years = currentYear - parseInt(draftYear, 10);
    if (!Number.isFinite(years) || years < 0) return 1;
    return Math.min(1, Math.max(0.3, years / 12));
}

function projectCareerValue(value, draftYear) {
    const maturity = careerMaturityFactor(draftYear);
    return value / maturity;
}

export function formatSignedScore(value) {
    const rounded = Math.round(value * 10) / 10;
    if (rounded > 0) return `+${rounded}`;
    return String(rounded);
}

function yearsSinceDraft(draftYear, currentYear = new Date().getFullYear()) {
    const years = currentYear - parseInt(draftYear, 10);
    return Number.isFinite(years) && years >= 0 ? years : null;
}

export function isRecentDraftProspect(draftYear, currentYear = new Date().getFullYear()) {
    const years = yearsSinceDraft(draftYear, currentYear);
    return years != null && years <= RECENT_DRAFT_PROSPECT_YEARS;
}

export function prospectDevelopmentScore(summary, isGoalie) {
    const { observed, components } = computeNhlObservedScore(summary, isGoalie);
    if (!components) {
        return {
            score: 0,
            observed: 0,
            components: null,
            multiplier: PROSPECT_LEAGUE_SCORE_MULTIPLIER,
        };
    }

    const score = observed * PROSPECT_LEAGUE_SCORE_MULTIPLIER;
    return {
        score: Math.round(score * 10) / 10,
        observed: Math.round(observed * 10) / 10,
        components: {
            ...components,
            multiplier: PROSPECT_LEAGUE_SCORE_MULTIPLIER,
        },
        multiplier: PROSPECT_LEAGUE_SCORE_MULTIPLIER,
    };
}

export function recentDraftProspectBonus(draftYear, actualOverallPick, nhlSummary, currentYear = new Date().getFullYear()) {
    const years = yearsSinceDraft(draftYear, currentYear);
    if (years == null || years > RECENT_DRAFT_PROSPECT_YEARS) {
        return {
            score: 0,
            years,
            recencyFactor: 0,
            pickFactor: 0,
            nhlFade: 0,
            eligible: false,
        };
    }

    const nhlGp = nhlSummary?.gamesPlayed || 0;
    const nhlFade = Math.max(0, 1 - nhlGp / 100);
    const pick = actualOverallPick ?? 120;
    const pickFactor = Math.max(4, 28 - pick * 0.18);
    const recencyFactor = (RECENT_DRAFT_PROSPECT_YEARS - years + 1) / (RECENT_DRAFT_PROSPECT_YEARS + 1);
    const score = Math.round(pickFactor * recencyFactor * nhlFade * 10) / 10;

    return {
        score,
        years,
        recencyFactor: Math.round(recencyFactor * 100) / 100,
        pickFactor: Math.round(pickFactor * 10) / 10,
        nhlFade: Math.round(nhlFade * 100) / 100,
        eligible: true,
    };
}

export function computeNhlObservedScore(summary, isGoalie) {
    if (!summary || !summary.gamesPlayed) {
        return { observed: 0, components: null };
    }

    const gp = summary.gamesPlayed || 0;
    if (isGoalie) {
        const sv = parseFloat(summary.savePercentage) || 0;
        const gaa = parseFloat(summary.goalsAgainstAverage) || 3;
        const gpContribution = gp * 0.12;
        const svContribution = Math.max(0, sv - 0.88) * 120;
        const gaaContribution = Math.max(0, 2.85 - gaa) * 18;
        return {
            observed: gpContribution + svContribution + gaaContribution,
            components: {
                type: "goalie",
                gamesPlayed: gp,
                savePercentage: sv,
                goalsAgainstAverage: gaa,
                gpContribution,
                svContribution,
                gaaContribution,
            },
        };
    }

    const points = summary.points || 0;
    const ppg = summary.pointsPerGame || 0;
    const pointsContribution = points * 0.45;
    const ppgContribution = ppg * 22;
    const gpContribution = gp * 0.06;
    return {
        observed: pointsContribution + ppgContribution + gpContribution,
        components: {
            type: "skater",
            gamesPlayed: gp,
            points,
            pointsPerGame: ppg,
            pointsContribution,
            ppgContribution,
            gpContribution,
        },
    };
}

export function nhlSuccessScore(summary, isGoalie, draftYear) {
    const { observed, components } = computeNhlObservedScore(summary, isGoalie);
    if (!components) {
        return {
            score: 0,
            observed: 0,
            maturity: 1,
            yearsSinceDraft: yearsSinceDraft(draftYear),
            components: null,
        };
    }

    const maturity = careerMaturityFactor(draftYear);
    return {
        score: projectCareerValue(observed, draftYear),
        observed,
        maturity,
        yearsSinceDraft: yearsSinceDraft(draftYear),
        components,
    };
}

export function scoreDraftPick(pick, identity, statsRows) {
    const isGoalie = pick.prospect.isGoalie;
    const draftYear = identity?.draftResult?.draft_year;
    const mockPick = pick.pickNumber;
    const actualPick = actualOverallPick(identity?.draftResult);
    const nhlSummary = statsRows?.length ? summarizeNhlCareer(statsRows, isGoalie) : null;
    const postDraftNonNhlSummary = draftYear && statsRows?.length
        ? summarizePostDraftNonNhlCareer(statsRows, draftYear, isGoalie)
        : null;
    const isRecentProspect = isRecentDraftProspect(draftYear);

    let pickValueScore = 0;
    if (actualPick != null) {
        // Positive when mock pick is later than NHL slot (steal); negative when you reach.
        pickValueScore = (mockPick - actualPick) * 2.2;
    }

    const nhlBreakdown = draftYear
        ? nhlSuccessScore(nhlSummary, isGoalie, draftYear)
        : {
            score: 0,
            observed: 0,
            maturity: 1,
            yearsSinceDraft: null,
            components: null,
        };
    const nhlScore = nhlBreakdown.score;

    const prospectDevelopment = isRecentProspect
        ? prospectDevelopmentScore(postDraftNonNhlSummary, isGoalie)
        : null;
    const prospectBonus = isRecentProspect
        ? recentDraftProspectBonus(draftYear, actualPick, nhlSummary)
        : null;
    const prospectDevelopmentScoreValue = prospectDevelopment?.score || 0;
    const prospectBonusScoreValue = prospectBonus?.score || 0;
    const prospectScore = prospectDevelopmentScoreValue + prospectBonusScoreValue;

    const pickDelta = actualPick != null ? mockPick - actualPick : null;
    let pickKind = "aligned";
    if (pickDelta > 0) pickKind = "steal";
    else if (pickDelta < 0) pickKind = "reach";

    const total = Math.round((pickValueScore + nhlScore + prospectScore) * 10) / 10;
    const draftResult = identity?.draftResult;

    return {
        total,
        pickValueScore: Math.round(pickValueScore * 10) / 10,
        nhlScore: Math.round(nhlScore * 10) / 10,
        prospectScore: Math.round(prospectScore * 10) / 10,
        actualPick,
        mockPick,
        realName: identity?.realName,
        playerId: identity?.playerId,
        isRecentProspect,
        nhlDraft: draftResult
            ? {
                year: draftResult.draft_year,
                round: draftResult.round,
                pick: draftResult.pick,
                overallPick: actualPick,
                teamName: draftResult.team_name,
            }
            : null,
        breakdown: {
            pickValue: {
                mockPick,
                actualPick,
                pickDelta,
                multiplier: 2.2,
                kind: pickKind,
                score: Math.round(pickValueScore * 10) / 10,
            },
            nhlCareer: {
                ...nhlBreakdown,
                score: Math.round(nhlBreakdown.score * 10) / 10,
                observed: Math.round(nhlBreakdown.observed * 10) / 10,
            },
            prospectDevelopment: prospectDevelopment
                ? {
                    ...prospectDevelopment,
                    score: prospectDevelopmentScoreValue,
                }
                : null,
            prospectBonus: prospectBonus?.eligible ? prospectBonus : null,
        },
    };
}

export function scoreDraftTeams(picks, identityMap, playerStatsByPlayerId, teamNames) {
    const teams = groupPicksByTeam(picks, teamNames).map((team) => {
        const pickScores = team.picks.map((pick) => {
            const identity = identityMap.get(pick.prospect.anonId);
            const playerId = identity?.playerId;
            const statsRows = playerId ? (playerStatsByPlayerId[playerId] ?? []) : [];
            return {
                pick,
                ...scoreDraftPick(pick, identity, statsRows),
            };
        });

        const totalScore = Math.round(
            pickScores.reduce((sum, row) => sum + row.total, 0) * 10
        ) / 10;

        return {
            ...team,
            pickScores,
            totalScore,
        };
    });

    return teams
        .sort((left, right) => right.totalScore - left.totalScore)
        .map((team, index) => ({
            ...team,
            rank: index + 1,
        }));
}
